import { gql, type TypedDocumentNode } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes, type CSSProperties } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Skeleton } from '@sorare/core/src/atoms/loader/Skeleton';
import { BodyL, LabelS } from '@sorare/core/src/atoms/typography';
import { HighlightOverlay } from '@sorare/core/src/components/onboarding/HighlightOverlay';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { FRONTEND_ASSET_HOST } from '@sorare/core/src/constants/assets';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery.ts';

import { InviteTitle } from '#src/components/series/inviteFriends/InviteTitle/index.tsx';

import type {
  InviteButtonFTUEQuery,
  InviteButtonFTUEQueryVariables,
} from './__generated__/index.graphql.ts';

const INVITE_BUTTON_FTUE_QUERY = gql`
  query InviteButtonFTUEQuery($sport: Sport!) {
    cardSet {
      id
      inviteTasksAsReferrerRewardConfigs(sport: $sport) {
        id
        rewardConfigs {
          id
          ...RewardConfigImage_anyRewardConfigInterface
          ...RewardConfigText_anyRewardConfigInterface
        }
      }
    }
  }
  ${RewardConfigImage.fragments.anyRewardConfigInterface}
  ${RewardConfigText.fragments.anyRewardConfigInterface}
` as TypedDocumentNode<InviteButtonFTUEQuery, InviteButtonFTUEQueryVariables>;

const wheelTicketUrl = `${FRONTEND_ASSET_HOST}/rewards/wheel-ticket-limited.png`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: var(--quadruple-unit);
  padding: var(--double-unit);
  opacity: 0;
  animation: ${fadeInUp} 0.6s 1s ease-in-out forwards;
  @media ${laptopAndAbove} {
    justify-content: center;
  }
`;

const MiddleSection = styled(Vertical).attrs({ center: true, gap: 1.5 })`
  flex: 1 0 auto;
  @media ${laptopAndAbove} {
    flex: unset;
  }
`;

const BonusCard = styled(Vertical).attrs({ center: true, gap: 2 })`
  backdrop-filter: blur(8px);
  background: var(--c-nl-500);
  border: 1px solid var(--c-nd-50);
  border-radius: var(--double-unit);
  padding: var(--triple-unit) var(--double-unit);
  width: 100%;
  max-width: 343px;
`;

const BonusLabel = styled(LabelS)`
  color: var(--c-set-onbase-primary);
  text-transform: uppercase;
`;

const TicketWrapper = styled(Horizontal).attrs({ gap: 0, center: true })`
  background: var(--c-set-surface-primary);
  border: 1px solid rgba(255, 255, 255, 0.17);
  border-radius: var(--radius-xxs);
  padding: var(--half-unit);
  box-shadow: 0 0 4px 0 #7029ff;
`;

const TicketImg = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

export const InviteButtonFTUE = () => {
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const { sport } = useSportContext();
  const shouldShow = !lifecycle?.[LIFECYCLE.sawInviteFriendsFTUE];

  const { data, loading } = useQuery(INVITE_BUTTON_FTUE_QUERY, {
    variables: { sport },
    skip: !shouldShow,
  });

  if (!shouldShow) {
    return null;
  }

  const lastRewardConfig = data?.cardSet?.inviteTasksAsReferrerRewardConfigs
    .at(-1)
    ?.rewardConfigs.at(0);

  return (
    <HighlightOverlay
      highlightNames={['InviteButton']}
      withDelay
      onClose={() => {
        updateLifecycle(LIFECYCLE.sawInviteFriendsFTUE, true);
      }}
      extras={[
        {
          nextTo: 'InviteButton',
          render: () => (
            <Overlay>
              <MiddleSection>
                <InviteTitle />
                <Skeleton loading={loading}>
                  {!!lastRewardConfig && (
                    <BodyL className="text-center">
                      <FormattedMessage
                        defaultMessage="Share your link and <b>earn up to {amount}</b> {unit} and more per invite!"
                        values={{
                          amount: (
                            <RewardConfigText
                              anyRewardConfig={lastRewardConfig}
                            />
                          ),
                          unit: (
                            <RewardConfigImage
                              anyRewardConfig={lastRewardConfig}
                              size={2.5}
                            />
                          ),
                          b: chunks => (
                            <BodyL as="span" bold>
                              {chunks}
                            </BodyL>
                          ),
                        }}
                      />
                    </BodyL>
                  )}
                </Skeleton>
              </MiddleSection>
              <BonusCard>
                <Vertical center gap={1.5}>
                  <BonusLabel bold>
                    <FormattedMessage defaultMessage="First friend bonus !" />
                  </BonusLabel>
                  <TicketWrapper>
                    <TicketImg src={wheelTicketUrl} alt="" />
                  </TicketWrapper>
                </Vertical>
                <BodyL className="text-center">
                  <FormattedMessage
                    defaultMessage="Earn a <golden>golden wheel spin</golden> when your <friend>first friend</friend> joins."
                    values={{
                      golden: chunks => (
                        <BodyL
                          as="span"
                          bold
                          className="text-gradient-color"
                          style={
                            {
                              '--text-gradient-color':
                                'linear-gradient(186deg, rgb(255, 255, 255) 1%, rgb(252, 255, 99) 37%, rgb(194, 105, 0) 102%)',
                            } as CSSProperties
                          }
                        >
                          {chunks}
                        </BodyL>
                      ),
                      friend: chunks => (
                        <BodyL
                          as="span"
                          bold
                          color="var(--c-set-onbase-primary)"
                        >
                          {chunks}
                        </BodyL>
                      ),
                    }}
                  />
                </BodyL>
              </BonusCard>
            </Overlay>
          ),
        },
      ]}
    />
  );
};
